// FN_GROUP_NAME is an environment variable that is set based on the github pull request
// number. And it will be made available only when a given PR has changes inside
// @highnote/server directory (via netlify-build.command.sh).
// The purpose of this environment variable is to group cloud functions together so that
// existing cloud functions are not overwritten by other PRs, giving each PR's preview link
// its own backend environment. It also allows us to delete all cloud functions associated
// with a PR when the PR is merged or closed.
export const functionGroupName = process.env.FN_GROUP_NAME || "";

// IMPORTANT: ensure that all function IDs are unique and do not contain hyphens,
// as hyphens are used to prefix IDs with the group name.
export const functionIds: Record<string, string> = {
  REFRESH_DROPBOX_TOKEN: "refreshDropboxToken",
  CLONE_DROPBOX_ENTITITES: "cloneDropboxEntities",
  HANDLE_DROPBOX_JOB: "handleDropboxJob",
  RETRY_DROPBOX_JOB: "retryDropboxJob",
  DELETE_DROPBOX_JOB: "deleteDropboxJob",

  ASSOCIATE_OAUTH_ACCOUNT: "associateOAuthAccount",

  CHECK_PROTECTED_ENTITY: "checkProtectedEntity",
  JOIN_PROTECTED_ENTITY: "joinProtectedEntity",

  DELETE_OR_REMOVE_ENTITIES: "deleteOrRemoveEntities",

  CREATE_SPACE: "createSpace",
  UPDATE_SPACE: "updateSpace",
  SET_SPACE_VISIBILITY: "setSpaceVisibility",
  DELETE_SPACE: "deleteSpace",
  /**
   * This function creates share keys on the server. Its id is deliberately
   * not in `onEventFunctionIDs`: it is a callable (a Cloud Function that
   * clients call directly), so it must stay group-prefixed, and each open
   * PR's preview then gets its own copy.
   */
  MINT_SPACE_SHARE_KEY: "mintSpaceShareKey",

  CREATE_TRACK: "createTrack",
  UPDATE_TRACK: "updateTrack",
  BULK_CREATE_OR_UPDATE_TRACKS: "bulkCreateOrUpdateTracks",
  DELETE_TRACK: "deleteTrack",
  GET_TRACK: "getTrack",
  ADD_TRACK_PLAYED: "addTrackPlayed",
  SHARE_TRACK: "shareTrack",
  UNSHARE_TRACK: "unshareTrack",
  UPDATE_SPACE_TRACK: "updateSpaceTrack",

  CREATE_OR_UPDATE_FILE: "createOrUpdateFile",
  BULK_CREATE_OR_UPDATE_FILES: "bulkCreateOrUpdateFiles",
  PROCESS_AUDIO_FILE: "processAudioFile",
  GENERATE_DOWNLOAD_LINK: "generateDownloadLink",
  ACKNOWLEDGE_DOWNLOAD_REQUEST: "acknowledgeDownloadRequest",
  GET_FILES: "getFiles",
  GET_STORAGE_FILES: "getStorageFiles",
  DELETE_STORAGE_FILE: "deleteStorageFile",
  GET_FILE_REFERENCES: "getFileReferences",

  GET_COMMENT: "getComment",
  CREATE_COMMENT: "createComment",
  UPDATE_COMMENT: "updateComment",
  DELETE_COMMENT: "deleteComment",
  GENERATE_SOCIAL_ASSETS: "generateSocialAssets",

  GET_ADS: "getAds",
  TRACK_AD_EVENT: "trackAdEvent",

  GET_KNOCK_AUTH_TOKEN: "getKnockAuthToken",
  GET_ALGOLIA_AUTH_TOKEN: "getAlgoliaAuthToken",
  GET_ALGOLIA_PUBLIC_SEARCH_AUTH_TOKEN: "getAlgoliaPublicSearchAuthToken",

  GET_FILE_URL: "getFileUrl",
  NOTIFY: "notify",

  REQUEST_USER_DELETION: "requestUserDeletion",
  UPDATE_USER: "updateUser",
  UPDATE_AUTH0_USER: "updateAuth0User",
  VERIFY_AUTH0_EMAIL: "verifyAuth0Email",
  RESET_AUTH0_PASSWORD: "resetAuth0Password",

  UPDATE_USER_SUBSCRIPTION: "payments_updateUserSubscription",
  GET_PRICE: "payments_getPrice",
  GET_PAYMENT_PORTAL_URL: "payments_getCustomerPortalUrl",
  GET_CHECKOUT_URL: "payments_getCheckoutUrl",

  AUTHENTICATE_USER: "authenticateUser",
  SEND_VERIFICATION_CODE: "sendVerificationCode",
  GET_USER_PUBLIC: "getUserPublic",
  GET_USER_PROFILE: "getUserProfile",
  GET_USERS_PUBLIC: "getUsersPublic",
  SEARCH_USERS: "searchUsers",
  UNLINK_ACCOUNT: "unlinkAccount",

  API_GET_SPACE: "api_getSpace",
  API_GET_TRACK: "api_getTrack",
  API_RESET_TEST_DATA: "api_resetTestData",

  // these are for functions that are automatically triggered by firestore events
  ON_USER_WRITE: "onUserWrite",
  ON_DOWNLOAD_REQUEST_UPDATE: "onDownloadRequestUpdate",
  ON_FILE_UPDATE: "onFileUpdate",
  ON_SPACE_UPDATE: "onSpaceUpdate",
  ON_TRACK_UPDATE: "onTrackUpdate",
  ON_COMMENT_UPDATE: "onCommentUpdate",
  ON_ENTITY_CREATE: "onEntityCreate",
  ON_ENTITY_DELETE: "onEntityDelete",
  ON_ENTITY_WRITE: "onEntityWrite",

  // this is triggered automatically by uploads to cloud storage bucket
  ON_FILE_UPLOAD: "onFileUpload",

  WEBHOOK_WELCOME_SURVEY: "webhook_welcomeSurvey",
  WEBHOOK_STRIPE: "webhook_stripe",
  WEBHOOK_KNOCK: "webhook_knock",
  WEBHOOK_REVENUE_CAT: "webhook_revenue_cat",
  WEBHOOK_TYPEFORM_SURVEY: "webhook_typeformSurvey",

  CLONE_ENTITY: "cloneEntity",
  MOVE_SELECTION_TO_ENTITY: "moveSelectionToEntity",
  BULK_MOVE_SELECTION_TO_ENTITY: "bulkMoveSelectionToEntity",
  TRANSCRIBE_AUDIO: "transcribeAudio",
  GENERATE_COMMENTS: "generateComments",
  ADMIN_DELETE_COMMENTS: "adminDeleteComments",

  // tasks that are triggered in Cloud Tasks
  TASK_TRANSCRIBE_WITH_MUSIC_AI: "taskTranscribeWithMusicAI",
  TASK_GENERATE_COMMENTS: "taskGenerateComments",
  TASK_SYNC_ENTITIES: "taskSyncEntities",
  TASK_HANDLE_WEBHOOK: "taskHandleWebhook",
  TASK_CLONE_STORAGE_FILE: "taskCloneStorageFile",
  TASK_GENERATE_PNG: "taskGeneratePng",
  TASK_GENERATE_VIDEO: "taskGenerateVideo",
  TASK_GENERATE_SOCIAL_ASSETS: "taskGenerateSocialAssets",
  TASK_SOCIAL_ASSETS_TIMEOUT: "taskSocialAssetsTimeout",
  TASK_SCRAPE_AIRTABLE_FIELD: "taskScrapeAirtableField",

  CREATE_OR_UPDATE_SUBMISSION_SETTINGS: "createOrUpdateSubmissionSettings",
  TOGGLE_SUBMISSION_SETTINGS: "toggleSubmissionSettings",
  GET_OR_CREATE_SUBMISSION_TOKEN: "getOrCreateSubmissionToken",

  GET_DISCOVER_PAGE_ITEMS: "getDiscoverPageItems",
  GET_DISCOVER_PAGE_ITEM_DETAIL: "getDiscoverPageItemDetail",
};

const onEventFunctionIDs = [
  functionIds.ON_USER_WRITE,
  functionIds.ON_DOWNLOAD_REQUEST_UPDATE,
  functionIds.ON_FILE_UPDATE,
  functionIds.ON_SPACE_UPDATE,
  functionIds.ON_TRACK_UPDATE,
  /* Without this entry the comment trigger deploys once for each open PR,
   * under that PR's own FN_GROUP_NAME prefix. Every comment write in the
   * dev project then triggers one run for each open preview. */
  functionIds.ON_COMMENT_UPDATE,
  functionIds.ON_ENTITY_CREATE,
  functionIds.ON_ENTITY_DELETE,
  functionIds.ON_ENTITY_WRITE,
  functionIds.ON_FILE_UPLOAD,
  functionIds.WEBHOOK_WELCOME_SURVEY,
  functionIds.WEBHOOK_STRIPE,
  functionIds.WEBHOOK_KNOCK,
  functionIds.WEBHOOK_REVENUE_CAT,
  functionIds.WEBHOOK_TYPEFORM_SURVEY,
  functionIds.TASK_TRANSCRIBE_WITH_MUSIC_AI,
  functionIds.TASK_GENERATE_COMMENTS,
  functionIds.TASK_SYNC_ENTITIES,
  functionIds.TASK_HANDLE_WEBHOOK,
  functionIds.TASK_CLONE_STORAGE_FILE,
];

export const FUNCTION_ID = Object.keys(functionIds).reduce<
  Record<string, string>
>((acc, key) => {
  // we should have only one instance of a function within a given GCP
  // if it is a function triggered by events (e.g., upload to bucket or webhooks).
  // so we just exclude these functions from the group to prevent having multiple
  // instances of these functions in the same project.
  if (onEventFunctionIDs.includes(functionIds[key])) {
    acc[key] = functionIds[key];
    return acc;
  }
  if (functionIds[key].includes("-")) {
    throw new Error("function ID cannot contain hyphens.");
  }
  acc[key] = functionGroupName
    ? `${functionGroupName}-${functionIds[key]}`
    : functionIds[key];
  return acc;
}, {});

export const MIN_FIREBASE_INSTANCES =
  process.env.DEPLOY_ENV === "production" ? 2 : 0;
